import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Cake, Heart, Sparkles, Palette, Flower2, Utensils, IndianRupee, StickyNote, Pencil } from "lucide-react";
import api from "@/lib/api";
import PlannerModal from "@/components/PlannerModal";
import RelationshipDialog from "@/components/RelationshipDialog";

export default function RelationshipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rel, setRel] = useState(null);
  const [history, setHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showPlanner, setShowPlanner] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const load = useCallback(async () => {
    const [r, h, p] = await Promise.all([
      api.get(`/relationships/${id}`),
      api.get(`/relationships/${id}/history`),
      api.get(`/planner/history?relationship_id=${id}`),
    ]);
    setRel(r.data); setHistory(h.data); setPlans(p.data);
  }, [id]);

  useEffect(() => { load().catch(() => navigate("/relationships")); }, [load, navigate]);

  if (!rel) return <div className="py-20 text-center text-charcoal/60">Loading…</div>;

  const upcoming = [
    { kind: "Birthday", date: rel.birthday, days: rel.birthday_days_left, Icon: Cake },
    { kind: "Anniversary", date: rel.anniversary, days: rel.anniversary_days_left, Icon: Heart },
  ].filter((x) => x.date);

  return (
    <div className="space-y-10" data-testid="relationship-detail-page">
      <button onClick={() => navigate("/relationships")} className="flex items-center gap-2 text-sm text-charcoal/60 hover:text-charcoal" data-testid="back-to-list">
        <ArrowLeft className="w-4 h-4" /> Back to People
      </button>

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface p-8">
          <div className="flex items-start gap-6">
            {rel.picture ? (
              <img src={rel.picture} className="w-24 h-24 rounded-full object-cover border border-warm" alt="" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-sage/20 flex items-center justify-center font-display text-3xl text-charcoal">
                {rel.name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-2">{rel.relationship_type}</p>
              <h1 className="font-display text-4xl text-charcoal">{rel.name}</h1>
              <div className="mt-4 flex flex-wrap gap-2">
                {rel.gender && <Chip>{rel.gender}</Chip>}
                {rel.budget && <Chip><IndianRupee className="w-3 h-3 mr-1 inline" strokeWidth={1.5} />{rel.budget}</Chip>}
              </div>
            </div>
            <button onClick={() => setShowEdit(true)} className="pill-btn pill-btn-secondary flex items-center gap-2" data-testid="edit-relationship-btn">
              <Pencil className="w-4 h-4" strokeWidth={1.5} /> Edit
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-warm grid sm:grid-cols-2 gap-4">
            <PrefRow icon={Palette} label="Favorite color" value={rel.fav_color} />
            <PrefRow icon={Flower2} label="Favorite flower" value={rel.fav_flower} />
            <PrefRow icon={Utensils} label="Favorite food" value={rel.fav_food} />
            <PrefRow icon={StickyNote} label="Notes" value={rel.notes} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-surface p-6 border-terracotta/40">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-3">AI Planner</p>
            <p className="font-display text-xl text-charcoal mb-4">Craft a personalised celebration.</p>
            <button onClick={() => setShowPlanner(true)} className="w-full pill-btn pill-btn-primary flex items-center justify-center gap-2" data-testid="open-planner-detail">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Plan celebration
            </button>
          </div>

          <div className="card-surface p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-3">Upcoming</p>
            {upcoming.length === 0 ? (
              <p className="text-sm text-charcoal/60">No dates on file yet.</p>
            ) : (
              <div className="space-y-3">
                {upcoming.map((u) => (
                  <div key={u.kind} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center">
                      <u.Icon className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className="font-display text-charcoal">{u.kind}</p>
                      <p className="text-xs text-charcoal/60">{u.date}</p>
                    </div>
                    <span className="text-sm font-semibold text-terracotta">{u.days}d</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl text-charcoal">Celebration history</h2>
            <p className="text-charcoal/60 text-sm mt-1">Past moments — the AI uses these to keep things fresh.</p>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="card-surface p-8 text-center text-charcoal/60" data-testid="history-empty">Nothing recorded yet. Bookings you complete will land here.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {history.map((h, i) => (
              <motion.div key={h.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="card-surface p-5" data-testid={`history-card-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-display text-lg text-charcoal">{h.occasion}</p>
                  <span className="text-xs text-charcoal/60">{h.date}</span>
                </div>
                <div className="text-sm space-y-1 text-charcoal/80">
                  {h.gift && <p><b>Gift:</b> {h.gift}</p>}
                  {h.cake && <p><b>Cake:</b> {h.cake}</p>}
                  {h.decoration && <p><b>Decor:</b> {h.decoration}</p>}
                  {h.budget && <p><b>Budget:</b> ₹{h.budget}</p>}
                  {h.notes && <p className="text-charcoal/60 italic mt-2">{h.notes}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {plans.length > 0 && (
        <section>
          <h2 className="font-display text-2xl text-charcoal mb-6">Previous AI plans</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {plans.map((p, i) => (
              <div key={p.id} className="card-surface p-5" data-testid={`plan-history-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta">{p.occasion} · {p.style}</p>
                  <span className="text-xs text-charcoal/60">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <p className="font-display text-lg text-charcoal">{p.plan?.title}</p>
                <p className="text-sm text-charcoal/70 mt-1 line-clamp-3">{p.plan?.plan_narrative}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {showPlanner && <PlannerModal relationship={rel} onClose={() => setShowPlanner(false)} />}
      {showEdit && <RelationshipDialog initial={rel} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </div>
  );
}

const Chip = ({ children }) => (
  <span className="rounded-full px-3 py-1 text-xs font-semibold bg-linen text-charcoal border border-warm">{children}</span>
);

const PrefRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-full bg-linen flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-charcoal/70" strokeWidth={1.5} />
    </div>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/50">{label}</p>
      <p className="text-charcoal">{value || <span className="text-charcoal/40">—</span>}</p>
    </div>
  </div>
);
